'use client';

import React, { type ChangeEvent, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  selectDomain,
  selectIsTenant,
  useDomainStore,
} from '@/store/use-domain.store';
import { MobileSearchOverlay } from '@/common/components/homeSearch/mobile-search-overlay.component';
import { useAppbarSearch } from '@/common/components/homeSearch/hooks/use-appbar-search';
import { SearchLoadingOverlay } from '@/common/components/homeSearch/search-loading-overlay.component';
import Button from '@/common/components/button';
import Input from '@/common/components/input';
import Spinner from '@/common/components/spinner/spinner.component';
import { useBackNavigation } from '@/common/hooks/use-back-navigation.hook';
import BackIcon from '~/public/assets/images/svg/arrow-right-line.svg';
import CloseIcon from '~/public/assets/images/svg/close-line.svg';
import SearchIcon from '~/public/assets/images/svg/search.svg';

interface SearchHeaderProps {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  clearSearch: () => void;
  isSearching: boolean;
  handleSubmit?: (text: string) => void;
  autoFocus?: boolean;
  onBack?: () => void;
}

export default function SearchHeader({
  onChange,
  value,
  isSearching,
  clearSearch,
  handleSubmit,
  autoFocus = false,
  onBack,
}: SearchHeaderProps) {
  const isTenant = useDomainStore(selectIsTenant);
  const config = useDomainStore(selectDomain);
  const { handleBack } = useBackNavigation();

  return (
    <div className="flex h-16 w-full items-center gap-3 bg-white px-4 py-1.5">
      <Button
        type="link"
        aria-label="بازگشت"
        className="!flex !h-6 !w-6 shrink-0 !items-center !justify-center !border-none !bg-transparent !p-0"
        onClick={() => {
          if (onBack) onBack();
          else handleBack('/');
        }}
      >
        <BackIcon className="h-6 w-6 text-plp-primary" />
      </Button>
      <div className="relative flex h-10 min-w-0 flex-1 items-center overflow-hidden rounded-md bg-gray-40">
        <Input
          value={value}
          onChange={onChange}
          autoFocus={autoFocus}
          dir="rtl"
          placeholder={
            isTenant ? `جست و جو در ${config?.title}` : 'جستجو در ویترو...'
          }
          onKeyDown={(key) => {
            if (key.code === 'Enter' || key.key === 'Enter') {
              handleSubmit?.(value);
            }
          }}
          styles={{
            input: {
              textAlign: 'right',
              direction: 'rtl',
            },
          }}
          classNames={{
            input: '!text-right placeholder:!text-right',
          }}
          className="h-auto border-none !bg-transparent !py-2.5 px-3 pl-10 text-right text-xs font-medium text-plp-primary"
          rootClassName="search-input alignRight w-full"
        />
        <div className="absolute left-3 top-1/2 flex !h-4 !w-4 -translate-y-1/2 items-center justify-center">
          {value.length === 0 && (
            <SearchIcon className="h-4 w-4 text-plp-muted" />
          )}
          {isSearching && value.length > 0 && (
            <Spinner
              containerClassName="!w-4"
              spinnerClassName="!text-plp-muted !w-4 !h-4"
            />
          )}
          {value.length > 0 && !isSearching && (
            <Button
              type="link"
              aria-label="پاک کردن جستجو"
              className="!flex !h-4 !w-4 !items-center !justify-center !border-none !bg-transparent !p-0 text-plp-muted"
              onClick={clearSearch}
            >
              <CloseIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function MobileSearchResultsHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryText = searchParams.get('queryText') || '';
  const [draftValue, setDraftValue] = useState(queryText);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const {
    handleSearch,
    searchResults,
    isLoading,
    isNavigating,
    beginNavigation,
    setIsOpen,
    goToSearchPage,
    resetSearchResults,
  } = useAppbarSearch();

  useEffect(() => {
    setDraftValue(queryText);
  }, [queryText]);

  const goBack = () => {
    router.push('/');
  };

  const submitOnResultsPage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const params = new URLSearchParams(searchParams.toString());
    params.set('queryText', trimmed);
    params.set('page', '1');
    params.delete('categoryId');
    beginNavigation();
    setOverlayOpen(false);
    router.push(`${pathname}?${params.toString()}`);
  };

  const openOverlay = () => {
    setDraftValue(queryText);
    setOverlayOpen(true);
    if (queryText.length >= 2) {
      handleSearch(queryText);
    }
  };

  return (
    <>
      <div className="flex h-16 w-full items-center gap-3 bg-white px-4 py-1.5 lg:hidden">
        <Button
          type="link"
          aria-label="بازگشت"
          className="!flex !h-6 !w-6 shrink-0 !items-center !justify-center !border-none !bg-transparent !p-0"
          onClick={goBack}
        >
          <BackIcon className="h-6 w-6 text-plp-primary" />
        </Button>
        <button
          type="button"
          dir="rtl"
          onClick={openOverlay}
          className="flex h-10 min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md bg-gray-40 px-3"
          aria-label="باز کردن جستجو"
        >
          <span className="min-w-0 flex-1 truncate text-right text-xs font-medium text-plp-primary">
            {queryText || 'جستجو در ویترو...'}
          </span>
          <SearchIcon className="h-4 w-4 shrink-0 text-plp-muted" />
        </button>
      </div>

      <div className="hidden lg:block">
        <SearchHeader
          value={draftValue}
          isSearching={false}
          clearSearch={() => {
            setDraftValue('');
          }}
          handleSubmit={submitOnResultsPage}
          onChange={(event) => {
            setDraftValue(event.target.value);
          }}
          onBack={goBack}
        />
      </div>

      {overlayOpen && !isNavigating && (
        <MobileSearchOverlay
          value={draftValue}
          isSearching={isLoading}
          searchResults={searchResults?.data}
          onClose={() => {
            setOverlayOpen(false);
            setIsOpen(false);
            setDraftValue(queryText);
          }}
          onClear={() => {
            setDraftValue('');
            resetSearchResults();
            setIsOpen(false);
          }}
          onSubmit={(text) => {
            goToSearchPage(text);
          }}
          onChange={(event) => {
            const next = event.target.value;
            setDraftValue(next);
            handleSearch(next);
          }}
          onNavigate={beginNavigation}
        />
      )}

      {isNavigating && <SearchLoadingOverlay />}
    </>
  );
}
